import { useCallback } from "react"
import * as yup from "yup"
import {
  useCompanyConnectorControllerAuthenticateCompanyConnectorMutation as useCompanyConnectorAuthMutation,
  useGuestCompanyConnectorControllerAuthenticateMutation as useGuestCompanyConnectorAuthMutation,
} from "~/ui-rtk/api/companyConnectorApi"
import { GuestTokenParam } from "../types"

// 10 digits, dashes/spaces optional (123-456-7890 or 1234567890). The backend
// normalizes and then live-verifies the account is linked under our MCC.
export const validationSchema = yup.object().shape({
  customerId: yup
    .string()
    .required("Customer ID is required!")
    .test(
      "customer-id-format",
      "Customer ID must be 10 digits (e.g. 123-456-7890)",
      value => (value ?? "").replace(/[^\d]/g, "").length === 10,
    ),
})

export type FormValues = yup.InferType<typeof validationSchema>

const initialValues: FormValues = {
  customerId: "",
}

type Params = {
  authCode: string
  companyConnectorId: string
} & GuestTokenParam

export const useConnect = () => {
  const [authAsync, { isLoading: isAuthLoading }] =
    useCompanyConnectorAuthMutation()
  const [guestAuthAsync, { isLoading: isGuestAuthLoading }] =
    useGuestCompanyConnectorAuthMutation()

  const authenticate = useCallback(async (params: Params) => {
    const { isGuest, companyConnectorId, authCode } = params
    if (isGuest) {
      await guestAuthAsync({
        id: companyConnectorId,
        token: params.token,
        authenticateCompanyConnectorDto: { authCode },
      }).unwrap()
    } else {
      await authAsync({
        id: companyConnectorId,
        authenticateCompanyConnectorDto: { authCode },
      }).unwrap()
    }
  }, [])

  const isLoading = isAuthLoading || isGuestAuthLoading

  return {
    initialValues,
    validationSchema,
    isLoading,
    authenticate,
  }
}
